// Pending referral code — captured when a friend lands on a referral
// link (/ref/:code or /join?ref=CODE) and consumed at web signup, where
// it's forwarded to api.signup({ referralCode }). Attribution itself runs
// server-side after the user row is created
// (users.attributeReferral); this is purely the web-side carry between the
// landing hit and the signup submit, which may be separated by an OAuth
// bounce, a tab reload, or a "come back later" gap — hence localStorage
// rather than in-memory or sessionStorage.

const KEY_PENDING_REFERRAL = 'wydm.pending_referral_code';

// Codes are 7-char Crockford base32 (see referrals.ts randomCode). We
// normalise to the same shape the minter emits — uppercased, with any
// stray separators/whitespace stripped — so a copy-pasted or lightly
// mangled code still matches. The backend is the authority on validity;
// this just avoids storing obvious junk (and caps length so a hostile
// link can't stuff localStorage).
function normalizeCode(raw: string): string | null {
  const cleaned = raw.trim().toUpperCase().replace(/[^0-9A-Z]/g, '');
  if (!cleaned) return null;
  return cleaned.slice(0, 16);
}

/** Persist a referral code captured from a landing link. No-ops on junk. */
export function storePendingReferralCode(raw: string): void {
  const code = normalizeCode(raw);
  if (!code) return;
  try {
    localStorage.setItem(KEY_PENDING_REFERRAL, code);
  } catch {
    // Private-mode / quota — attribution is best-effort, so swallow.
  }
}

/**
 * Read WITHOUT clearing. For callers that need to know the code but
 * aren't the moment of attribution — specifically the app-download CTAs
 * (lib/appDownloadLink.ts), which thread the code into the store link so
 * an install can still be attributed. Those must not consume it: the
 * visitor may well come back and sign up on the web afterwards, and that
 * path is the deterministic one we'd rather win.
 */
export function peekPendingReferralCode(): string | undefined {
  try {
    return localStorage.getItem(KEY_PENDING_REFERRAL) ?? undefined;
  } catch {
    return undefined;
  }
}

/**
 * Read and clear the pending referral code. Returns undefined when none
 * is stored. Single-use by design: signup reads it exactly once so a
 * later unrelated signup in the same browser isn't mis-attributed.
 */
export function takePendingReferralCode(): string | undefined {
  try {
    const code = localStorage.getItem(KEY_PENDING_REFERRAL);
    if (!code) return undefined;
    localStorage.removeItem(KEY_PENDING_REFERRAL);
    return code;
  } catch {
    return undefined;
  }
}
